/**
 * CustomerOverviewTab - Overview tab content for customer detail modal
 * Extracted from CustomersPage.js
 */
import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PhoneInput } from '@/components/ui/phone-input';
import { CheckCircle, XCircle, Mail, MessageCircle, Bell, Download, PenLine, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { CustomerPaymentCard } from './CustomerPaymentCard';
import ConsentActionsDialog from './ConsentActionsDialog';
import CustomerMessageHistory from './CustomerMessageHistory';
import CustomerManageLinks from './CustomerManageLinks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerOverviewTab = ({
  customerDetails,
  setCustomerDetails,
  selectedCustomer,
  editMode,
  setEditMode,
  editFormData,
  setEditFormData,
  studios = [],
  deleteConfirmStep,
  setDeleteConfirmStep,
  deactivateConfirmStep,
  setAddChildDialogOpen,
  setChildFormData,
  handleDeleteChild,
  handleEditSubmit,
  handleDeleteConfirm
}) => {
  const [consents, setConsents] = React.useState([]);
  const [consentDialog, setConsentDialog] = React.useState(null); // {mode, formId, formTitle}
  const [invitingComplete, setInvitingComplete] = React.useState(false);

  const handleInviteComplete = async () => {
    if (invitingComplete) return;
    const cid = customerDetails?.id;
    if (!cid) return;
    setInvitingComplete(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/customers/${cid}/invite`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(res.data?.message || 'Einladung gesendet.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Einladung konnte nicht gesendet werden.');
    } finally {
      setInvitingComplete(false);
    }
  };

  const loadConsents = React.useCallback(() => {
    if (!customerDetails?.id) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/customers/${customerDetails.id}/consents`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setConsents(res.data.forms || []))
      .catch(() => setConsents([]));
  }, [customerDetails?.id]);

  React.useEffect(() => { loadConsents(); }, [loadConsents]);

  // Unterschriebene Erklärung als PDF herunterladen (Archiv/Haftungsfälle)
  const downloadConsentPdf = async (c) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/consents/${c.signed.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Einverstaendnis_${c.id}_${(customerDetails?.first_name || '')}_${(customerDetails?.last_name || '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF-Download fehlgeschlagen');
    }
  };

  const formattedCustomerBirthDate = useMemo(() => {
    if (!customerDetails?.date_of_birth) return '-';
    try {
      return new Date(customerDetails.date_of_birth).toLocaleDateString('de-DE');
    } catch (error) {
      return '-';
    }
  }, [customerDetails?.date_of_birth]);

  const formattedChildren = useMemo(() => {
    return (customerDetails?.children || []).map((child, index) => {
      let birthDate = '-';
      if (child.date_of_birth) {
        try {
          birthDate = new Date(child.date_of_birth).toLocaleDateString('de-CH', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric'
          });
        } catch (error) {
          birthDate = '-';
        }
      }
      return {
        ...child,
        index,
        birthDate
      };
    });
  }, [customerDetails?.children]);

  const isCustomerVerified = useMemo(() => {
    const status = (customerDetails?.verification_status || '').toLowerCase();
    if (['completed', 'complete', 'verified'].includes(status)) return true;

    // Fallbacks for legacy/live data where verification_status was not maintained consistently
    if (customerDetails?.email_verified === true) return true;
    if (customerDetails?.google_id) return true; // Google-Login users are verified automatically
    if (customerDetails?.portal_registered_at) return true; // Portal registration completed

    return false;
  }, [customerDetails]);
  
  // Handle resend verification
  const handleResendVerification = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/customers/${customerDetails.id}/resend-verification`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Verifizierungs-Link wurde erneut gesendet!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Senden');
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-overview-tab">
      {/* Kopf: Avatar, Name, Status-Chips */}
      <div className="p-4 rounded-xl border border-gray-200 bg-gradient-to-r from-emerald-50 to-white">
        <div className="flex flex-wrap items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xl font-bold shrink-0 overflow-hidden" data-testid="customer-avatar">
            {customerDetails.photo_url
              ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api${customerDetails.photo_url}`} alt="" className="h-full w-full object-cover" data-testid="customer-avatar-photo" />
              : `${(customerDetails.first_name || '?')[0] || ''}${(customerDetails.last_name || '')[0] || ''}`.toUpperCase()}
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900 truncate">
              {customerDetails.first_name} {customerDetails.last_name}
            </h2>
            <p className="text-sm text-gray-500">
              Kunden-Nr. {customerDetails.customer_number || '—'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 ml-auto items-center">
            {consents.map(c => (
              <span
                key={c.id}
                title={c.signed ? `Unterschrieben am ${new Date(c.signed.signed_at).toLocaleDateString('de-CH')} — Klick auf ⬇ für PDF` : 'Noch nicht unterschrieben — ✍️ = vor Ort unterschreiben, 📤 = Link senden'}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${c.signed ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}
                data-testid={`consent-badge-${c.id}`}
              >
                {c.signed ? <CheckCircle size={13} /> : <XCircle size={13} />} {c.id === 'kryo' ? 'Kryo' : c.id === 'ems' ? 'EMS' : c.id === 'haftung' ? 'Haftung' : c.id === 'agb' ? 'AGB' : c.id}
                {c.signed && (
                  <button
                    type="button"
                    onClick={() => downloadConsentPdf(c)}
                    className="ml-0.5 hover:text-emerald-900"
                    title="Unterschriebene Erklärung als PDF herunterladen"
                    data-testid={`consent-pdf-download-${c.id}`}
                  >
                    <Download size={13} />
                  </button>
                )}
                {!c.signed && (
                  <>
                    <button
                      type="button"
                      onClick={() => setConsentDialog({ mode: 'sign', formId: c.id, formTitle: c.title || c.id })}
                      className="ml-0.5 hover:text-orange-900"
                      title="Vor Ort unterschreiben (iPad/Tablet)"
                      data-testid={`consent-sign-onsite-${c.id}`}
                    >
                      <PenLine size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setConsentDialog({ mode: 'request', formId: c.id, formTitle: c.title || c.id })}
                      className="hover:text-orange-900"
                      title="Unterschrift-Link per WhatsApp/E-Mail senden"
                      data-testid={`consent-request-link-${c.id}`}
                    >
                      <Send size={13} />
                    </button>
                  </>
                )}
              </span>
            ))}
            {isCustomerVerified ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="overview-verification-status-verified">
                <CheckCircle size={13} /> Verifiziert
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border bg-orange-50 text-orange-700 border-orange-200" data-testid="overview-verification-status-unverified">
                <XCircle size={13} /> Nicht verifiziert
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResendVerification}
                  className="h-5 px-1 text-orange-700 hover:bg-orange-100"
                  data-testid="overview-resend-verification"
                >
                  <Mail size={12} className="mr-1" /> Link senden
                </Button>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Zahlung: direkt änderbar */}
      <CustomerPaymentCard customerDetails={customerDetails} setCustomerDetails={setCustomerDetails} />

      {/* 08/2026: Einladung zur Konto-Vervollständigung (nur bei unvollständigem/inaktivem Konto) */}
      {(() => {
        const c = customerDetails || {};
        const incomplete = !c.profile_completed && (
          c.status !== 'active' || !c.first_name || !c.last_name || !c.phone
        );
        if (!incomplete) return null;
        return (
          <div className="flex gap-2 p-4 bg-amber-50 rounded-lg border border-amber-200" data-testid="invite-complete-card">
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-900">Konto unvollständig</p>
              <p className="text-xs text-amber-700">Einladung senden (E-Mail & WhatsApp) — der Kunde vervollständigt sein Profil per Direkt-Link ohne Login.</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={invitingComplete}
              onClick={handleInviteComplete}
              className="text-amber-700 border-amber-300 hover:bg-amber-100"
              data-testid="invite-complete-btn"
            >
              <Send size={14} className="mr-1" />
              {invitingComplete ? 'Wird gesendet…' : 'Einladung senden'}
            </Button>
          </div>
        );
      })()}

      {/* Edit Mode */}
      {!editMode ? (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-600">Vorname</Label>
            <p className="font-medium">{customerDetails.first_name || selectedCustomer.first_name || '-'}</p>
          </div>
          <div>
            <Label className="text-gray-600">Nachname</Label>
            <p className="font-medium">{customerDetails.last_name || selectedCustomer.last_name || '-'}</p>
          </div>
          <div>
            <Label className="text-gray-600">E-Mail</Label>
            <p className="font-medium">{customerDetails.email || selectedCustomer.email}</p>
          </div>
          <div>
            <Label className="text-gray-600">Telefon</Label>
            <p className="font-medium" data-testid="customer-view-phone">{customerDetails.phone || selectedCustomer.phone || '-'}</p>
          </div>
          <div>
            <Label className="text-gray-600">Geburtsdatum</Label>
            <p className="font-medium">
              {customerDetails.date_of_birth 
                ? formattedCustomerBirthDate
                : '-'
              }
            </p>
          </div>
          <div>
            <Label className="text-gray-600">Strasse</Label>
            <p className="font-medium">{customerDetails.street || '-'}</p>
          </div>
          <div>
            <Label className="text-gray-600">PLZ</Label>
            <p className="font-medium">{customerDetails.postal_code || '-'}</p>
          </div>
          <div>
            <Label className="text-gray-600">Ort</Label>
            <p className="font-medium">{customerDetails.city || '-'}</p>
          </div>
          
          {/* Notfallkontakt in Ansicht */}
          {(customerDetails.emergency_contact?.name || customerDetails.emergency_contact?.phone) && (
            <div className="col-span-3 border-t pt-3 mt-2">
              <Label className="text-gray-600 flex items-center gap-1">🆘 Notfallkontakt</Label>
              <p className="font-medium">
                {customerDetails.emergency_contact?.name || '-'} 
                {customerDetails.emergency_contact?.phone && ` | ${customerDetails.emergency_contact.phone}`}
              </p>
            </div>
          )}
          
          {/* EMS Anzugsgrösse in Ansicht */}
          {customerDetails.suit_size && (
            <div className="col-span-2 border-t pt-3 mt-2">
              <Label className="text-gray-600 flex items-center gap-1">👕 EMS Anzugsgrösse</Label>
              <p className="font-medium text-lg text-teal-600">{customerDetails.suit_size}</p>
            </div>
          )}
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Kundennummer</Label>
            <Input
              disabled
              value={customerDetails?.customer_number || ''}
            />
          </div>
          <div>
            <Label>Vorname</Label>
            <Input
              value={editFormData.first_name}
              onChange={(e) => setEditFormData({...editFormData, first_name: e.target.value})}
              disabled={customerDetails.agb_accepted && customerDetails.liability_waiver_signed}
            />
          </div>
          <div>
            <Label>Nachname</Label>
            <Input
              value={editFormData.last_name}
              onChange={(e) => setEditFormData({...editFormData, last_name: e.target.value})}
              disabled={customerDetails.agb_accepted && customerDetails.liability_waiver_signed}
            />
          </div>
          <div>
            <Label>E-Mail</Label>
            <Input
              value={editFormData.email}
              onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
              disabled={customerDetails.agb_accepted && customerDetails.liability_waiver_signed}
            />
          </div>
          <div>
            <Label>Telefon</Label>
            <PhoneInput
              value={editFormData.phone}
              onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
            />
          </div>
          <div>
            <Label>Geburtsdatum</Label>
            <Input
              type="date"
              value={editFormData.date_of_birth}
              onChange={(e) => setEditFormData({...editFormData, date_of_birth: e.target.value})}
            />
          </div>
          
          {/* Geschlecht */}
          <div>
            <Label>Geschlecht *</Label>
            <div className="flex gap-2 mt-1">
              <Button
                type="button"
                variant={editFormData.gender === 'male' ? 'default' : 'outline'}
                onClick={() => setEditFormData({...editFormData, gender: 'male'})}
                className={`flex-1 ${editFormData.gender === 'male' ? 'bg-blue-500 text-white hover:bg-blue-600' : ''}`}
                size="sm"
              >
                ♂ Mann
              </Button>
              <Button
                type="button"
                variant={editFormData.gender === 'female' ? 'default' : 'outline'}
                onClick={() => setEditFormData({...editFormData, gender: 'female'})}
                className={`flex-1 ${editFormData.gender === 'female' ? 'bg-pink-500 text-white hover:bg-pink-600' : ''}`}
                size="sm"
              >
                ♀ Frau
              </Button>
            </div>
          </div>
          
          <div>
            <Label>🌍 Bevorzugte Sprache</Label>
            <div className="flex gap-2 mt-1">
              {[['de', 'DE'], ['en', 'EN'], ['fr', 'FR'], ['it', 'IT']].map(([code, label]) => (
                <Button
                  key={code}
                  type="button"
                  size="sm"
                  data-testid={`admin-customer-lang-${code}`}
                  variant={(editFormData.preferred_language || 'de') === code ? 'default' : 'outline'}
                  onClick={() => setEditFormData({...editFormData, preferred_language: code})}
                  className="flex-1"
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>

          <div>
            <Label>Strasse</Label>
            <Input
              value={editFormData.street}
              onChange={(e) => setEditFormData({...editFormData, street: e.target.value})}
            />
          </div>
          <div>
            <Label>PLZ</Label>
            <Input
              value={editFormData.postal_code}
              onChange={(e) => setEditFormData({...editFormData, postal_code: e.target.value})}
            />
          </div>
          <div>
            <Label>Ort</Label>
            <Input
              value={editFormData.city}
              onChange={(e) => setEditFormData({...editFormData, city: e.target.value})}
            />
          </div>

          {studios.length > 1 && (
            <div className="col-span-3 mt-2">
              <Label className="flex items-center gap-1">📍 Studio (Heim-Standort)</Label>
              <Select
                value={editFormData.studio_id || ''}
                onValueChange={(value) => setEditFormData({ ...editFormData, studio_id: value })}
              >
                <SelectTrigger data-testid="customer-studio-select">
                  <SelectValue placeholder="Studio wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {studios.map((s) => (
                    <SelectItem
                      key={s.id}
                      value={s.id}
                      data-testid={`customer-studio-option-${s.short_name || s.id}`}
                    >
                      📍 {s.short_name || s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Online-Buchungen zeigen Services dieses Studios. Default = Wetzikon.
              </p>
            </div>
          )}
          
          {/* Notfallkontakt */}
          <div className="col-span-3 border-t pt-4 mt-2">
            <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              🆘 Notfallkontakt
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editFormData.emergency_contact_name}
                  onChange={(e) => setEditFormData({...editFormData, emergency_contact_name: e.target.value})}
                  placeholder="Max Muster"
                />
              </div>
              <div>
                <Label>Telefon</Label>
                <PhoneInput
                  value={editFormData.emergency_contact_phone}
                  onChange={(e) => setEditFormData({...editFormData, emergency_contact_phone: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          <div>
            <Label>Grösse (cm)</Label>
            <Input
              type="number"
              placeholder="z.B. 175"
              value={editFormData.height}
              onChange={(e) => setEditFormData({...editFormData, height: e.target.value ? parseFloat(e.target.value) : ''})}
              data-testid="customer-height-input"
            />
          </div>
          <div>
            <Label>EMS Anzugsgrösse</Label>
            <Input
              type="text"
              placeholder="z.B. 4, 2.5 oder 4NEU"
              value={editFormData.suit_size}
              onChange={(e) => setEditFormData({...editFormData, suit_size: e.target.value})}
              data-testid="customer-suit-size-input"
            />
          </div>
        </div>

        {/* Nachrichten-Verlauf (an diesen Kunden gesendete E-Mails & WhatsApp) */}
        {customerDetails?.id && (
          <div className="mt-6">
            <CustomerMessageHistory customerId={customerDetails.id} />
          </div>
        )}

        {/* Termin-Verwaltungs-Beziehungen (darf für einander absagen/buchen) */}
        {customerDetails?.id && (
          <div className="mt-6">
            <CustomerManageLinks
              customerId={customerDetails.id}
              customerName={`${customerDetails.first_name || ''} ${customerDetails.last_name || ''}`.trim()}
            />
          </div>
        )}

        {/* Kommunikations-Präferenzen */}
        <div className="mt-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Kommunikations-Präferenzen
          </h3>
          
          <div className="space-y-4">
            {/* Bevorzugter Kanal — Unified Notification-Channel
               (User-Wunsch 14.02.2026: Admin-Portal soll dieselbe Logik wie
                Mobile-App + Customer-Portal nutzen. Ein einziger Selector
                steuert Termin-Bestätigungen, Erinnerungen und alle
                transaktionalen Nachrichten. Stornierungen gehen aus
                Sicherheitsgründen IMMER per Email + WhatsApp.) */}
            <div>
              <Label className="text-base font-medium">Bevorzugter Kanal</Label>
              <p className="text-xs text-gray-500 mt-1 mb-3">
                Für Termin-Bestätigungen, Erinnerungen und Sharing-Einladungen.
              </p>
              <div className="grid grid-cols-3 gap-2" data-testid="notification-channel-group">
                {[
                  { value: 'email', label: 'Nur Email', icon: Mail, color: 'blue' },
                  { value: 'whatsapp', label: 'Nur WhatsApp', icon: MessageCircle, color: 'green' },
                  { value: 'both', label: 'Beide', icon: Bell, color: 'amber' },
                ].map(({ value, label, icon: Icon, color }) => {
                  const current = editFormData.notification_channel || 'both';
                  const selected = current === value;
                  const baseColors = {
                    blue: selected ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50 text-gray-600',
                    green: selected ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 hover:bg-gray-50 text-gray-600',
                    amber: selected ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 hover:bg-gray-50 text-gray-600',
                  };
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setEditFormData({ ...editFormData, notification_channel: value })}
                      className={`flex flex-col items-center gap-1 p-3 border-2 rounded-lg transition ${baseColors[color]}`}
                      data-testid={`notification-channel-${value}`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-xs font-medium">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Master-Schalter: Terminerinnerungen (User-Wunsch 06/2026).
               AUS = keine wiederkehrenden Termin-/Trainingserinnerungen mehr
               (E-Mail + WhatsApp). Bestätigungen/Stornos laufen weiter. */}
            <div className="flex items-center justify-between p-3 bg-white rounded border-2 border-amber-200">
              <div className="flex items-center gap-2">
                <Bell className="h-4 w-4 text-amber-600" />
                <div>
                  <span className="text-sm font-medium">Terminerinnerungen senden</span>
                  <p className="text-xs text-gray-500">Aus = keine wiederkehrenden Erinnerungen (E-Mail + WhatsApp). Bestätigungen &amp; Stornos laufen weiter.</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={editFormData.reminders_enabled !== false}
                  onChange={(e) => setEditFormData({ ...editFormData, reminders_enabled: e.target.checked })}
                  className="sr-only peer"
                  data-testid="reminders-master-toggle"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
              </label>
            </div>

            {/* Transaktionale Benachrichtigungen pro Kunde (Admin-Steuerung
               06/2026): Alles was der Kunde in der App schalten kann, kann
               auch der Admin/Trainer hier für den Kunden setzen. */}
            <div>
              <Label className="text-base font-medium">Benachrichtigungen</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">
                Steuere, welche Nachrichten dieser Kunde erhält. Kanal (E-Mail/WhatsApp) gemäss „Bevorzugter Kanal".
              </p>
              <div className="space-y-2">
                {/* Terminbestätigungen */}
                <div className="flex items-center justify-between p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">Terminbestätigungen</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.appointment_confirmation_notifications !== false}
                      onChange={(e) => setEditFormData({ ...editFormData, appointment_confirmation_notifications: e.target.checked })}
                      className="sr-only peer"
                      data-testid="appointment-confirmation-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Terminänderungen & Stornos */}
                <div className="flex items-center justify-between p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">Terminänderungen &amp; Stornos</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.appointment_changes_notifications !== false}
                      onChange={(e) => setEditFormData({ ...editFormData, appointment_changes_notifications: e.target.checked })}
                      className="sr-only peer"
                      data-testid="appointment-changes-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                {/* Zahlungsbestätigungen */}
                <div className="flex items-center justify-between p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">Zahlungsbestätigungen</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.payment_confirmation_enabled !== false}
                      onChange={(e) => setEditFormData({ ...editFormData, payment_confirmation_enabled: e.target.checked })}
                      className="sr-only peer"
                      data-testid="payment-confirmation-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Newsletter - BEIDE Kanäle möglich */}
            <div>
              <Label className="text-base font-medium">Newsletter</Label>
              <div className="mt-2 space-y-2">
                <div className="flex items-center justify-between p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">Newsletter per Email</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.email_newsletters_enabled !== false}
                      onChange={(e) => setEditFormData({...editFormData, email_newsletters_enabled: e.target.checked})}
                      className="sr-only peer"
                      data-testid="newsletter-email-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-2 bg-white rounded border">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Newsletter per WhatsApp</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editFormData.whatsapp_newsletters_enabled !== false}
                      onChange={(e) => setEditFormData({...editFormData, whatsapp_newsletters_enabled: e.target.checked})}
                      className="sr-only peer"
                      data-testid="newsletter-whatsapp-toggle"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Hinweis */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded">
              <p className="text-xs text-amber-900">
                <strong>ℹ️ Hinweis:</strong> Stornierungen und Serien-Stornierungen werden aus Sicherheitsgründen IMMER per Email UND WhatsApp gesendet — unabhängig vom gewählten Kanal.
              </p>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between pt-4 mt-6 border-t">
        <div className="flex gap-2">
          {!editMode ? (
            <Button onClick={() => {
              setEditMode(true);
              setEditFormData({
                ...customerDetails,
                notification_channel: customerDetails.notification_channel || 'both',
                reminders_enabled: customerDetails.reminders_enabled !== false,
                email_reminders_enabled: customerDetails.email_reminders_enabled !== false,
                whatsapp_reminders_enabled: customerDetails.whatsapp_reminders_enabled !== false,
                email_newsletters_enabled: customerDetails.email_newsletters_enabled !== false,
                whatsapp_newsletters_enabled: customerDetails.whatsapp_newsletters_enabled !== false,
                appointment_changes_notifications: customerDetails.appointment_changes_notifications !== false,
                appointment_confirmation_notifications: customerDetails.appointment_confirmation_notifications !== false,
                payment_confirmation_enabled: customerDetails.payment_confirmation_enabled !== false
              });
            }} data-testid="edit-customer-button">
              Bearbeiten
            </Button>
          ) : (
            <>
              <Button onClick={handleEditSubmit} data-testid="save-customer-button">
                Speichern
              </Button>
              <Button variant="outline" onClick={() => setEditMode(false)}>
                Abbrechen
              </Button>
            </>
          )}
        </div>
        
        {/* Right side buttons: Delete (edit mode) or Deactivate (view mode) */}
        <div className="flex gap-2">
          {/* Delete Button - Only in edit mode */}
          {editMode && (
            <Button 
              variant="destructive"
              size="sm"
              onClick={handleDeleteConfirm}
              data-testid="delete-customer-button"
            >
              {deleteConfirmStep === 0 && 'Löschen'}
              {deleteConfirmStep === 1 && 'Klicken Sie erneut'}
              {deleteConfirmStep === 2 && 'Endgültig löschen'}
            </Button>
          )}
        </div>
      </div>
      
      {/* Kinder (für Geburtstagsgratulationen) */}
      <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h4 className="font-semibold text-purple-900">👶 Kinder</h4>
            <p className="text-sm text-purple-700">Für Geburtstagsgratulationen</p>
          </div>
          {!editMode && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setChildFormData({ name: '', date_of_birth: '' });
                setAddChildDialogOpen(true);
              }}
            >
              ➕ Kind hinzufügen
            </Button>
          )}
        </div>
        {formattedChildren.length > 0 ? (
          <div className="space-y-2" data-testid="overview-children-list">
            {formattedChildren.map((child) => (
              <div key={child.index} className="flex justify-between items-center bg-white p-3 rounded border border-purple-300" data-testid={`overview-child-${child.index}`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">👶</span>
                  <div>
                    <div className="font-semibold">{child.name}</div>
                    <div className="text-sm text-gray-600">
                      Geburtstag: {child.birthDate}
                    </div>
                  </div>
                </div>
                {!editMode && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteChild(child.index)}
                    data-testid={`overview-child-delete-${child.index}`}
                    className="text-red-600 hover:text-red-700"
                  >
                    🗑️
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-purple-600 bg-white p-3 rounded border border-purple-200" data-testid="overview-children-empty">
            Keine Kinder hinterlegt
          </div>
        )}
      </div>

      {deleteConfirmStep > 0 && (
        <div className="bg-red-50 border border-red-200 p-4 rounded-lg mt-4">
          <p className="text-red-800 font-medium">
            ⚠️ Kunde wird gelöscht — 30 Tage wiederherstellbar unter System → Gelöschte Konten
          </p>
          <p className="text-sm text-red-600 mt-1">
            Der Kunde wird per E-Mail über die Löschung benachrichtigt. Bestätigungen: {deleteConfirmStep} / 3
          </p>
        </div>
      )}
      
      {deactivateConfirmStep > 0 && (
        <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg mt-4">
          <p className="text-orange-800 font-medium">
            ⚠️ Warnung: Dieser Kunde wird deaktiviert!
          </p>
          <p className="text-sm text-orange-600 mt-1">
            Bestätigungen: {deactivateConfirmStep} / 3
          </p>
        </div>
      )}
      {consentDialog && (
        <ConsentActionsDialog
          open={!!consentDialog}
          onClose={() => setConsentDialog(null)}
          mode={consentDialog.mode}
          formId={consentDialog.formId}
          formTitle={consentDialog.formTitle}
          customer={customerDetails}
          onSigned={(fid) => {
            loadConsents();
            if (fid === 'haftung') setCustomerDetails?.(prev => ({ ...prev, liability_waiver_signed: true }));
          }}
        />
      )}
    </div>
  );
};

export default React.memo(CustomerOverviewTab);
